import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { 
  MessageCircle,
  Check,
  X,
  Settings,
  Play,
  Pause,
  ExternalLink,
  Users,
  Activity,
  Lock,
  Unlock,
  TrendingUp
} from "lucide-react";
import { TelegramIcon, XTwitterIcon, FacebookIcon, GoogleMapsIcon } from "@/components/PlatformIcons";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category names for display - bidirectional mapping
const categoryNames = {
  // English IDs
  news_media: { en: "News", ar: "أخبار" },
  blogs: { en: "Blogs", ar: "مدونات" },
  humor_entertainment: { en: "Entertainment", ar: "ترفيه" },
  technologies: { en: "Tech", ar: "تقنية" },
  economics: { en: "Economics", ar: "اقتصاد" },
  business_startups: { en: "Business", ar: "أعمال" },
  cryptocurrencies: { en: "Crypto", ar: "كريبتو" },
  travel: { en: "Travel", ar: "سفر" },
  marketing_pr: { en: "Marketing", ar: "تسويق" },
  psychology: { en: "Psychology", ar: "نفسية" },
  design: { en: "Design", ar: "تصميم" },
  politics: { en: "Politics", ar: "سياسة" },
  art: { en: "Art", ar: "فن" },
  law: { en: "Law", ar: "قانون" },
  education: { en: "Education", ar: "تعليم" },
  books: { en: "Books", ar: "كتب" },
  linguistics: { en: "Languages", ar: "لغات" },
  career: { en: "Career", ar: "وظائف" },
  edutainment: { en: "Edutainment", ar: "تعليم ترفيهي" },
  courses_guides: { en: "Courses", ar: "دورات" },
  sport: { en: "Sport", ar: "رياضة" },
  fashion_beauty: { en: "Fashion", ar: "موضة" },
  medicine: { en: "Medicine", ar: "طب" },
  health_fitness: { en: "Health", ar: "صحة" },
  other: { en: "Other", ar: "أخرى" },
  // Arabic names as keys for reverse lookup
  "تجارة": { en: "Commerce", ar: "تجارة" },
  "تقنية": { en: "Technology", ar: "تقنية" },
  "تسويق": { en: "Marketing", ar: "تسويق" },
  "أخبار": { en: "News", ar: "أخبار" },
  "ترفيه": { en: "Entertainment", ar: "ترفيه" },
  "اقتصاد": { en: "Economics", ar: "اقتصاد" },
  "كريبتو": { en: "Crypto", ar: "كريبتو" },
  "سفر": { en: "Travel", ar: "سفر" },
  "تصميم": { en: "Design", ar: "تصميم" },
  "سياسة": { en: "Politics", ar: "سياسة" },
  "فن": { en: "Art", ar: "فن" },
  "قانون": { en: "Law", ar: "قانون" },
  "تعليم": { en: "Education", ar: "تعليم" },
  "كتب": { en: "Books", ar: "كتب" },
  "رياضة": { en: "Sport", ar: "رياضة" },
  "موضة": { en: "Fashion", ar: "موضة" },
  "طب": { en: "Medicine", ar: "طب" },
  "صحة": { en: "Health", ar: "صحة" },
  "أعمال": { en: "Business", ar: "أعمال" },
  "وظائف": { en: "Career", ar: "وظائف" },
  // Common English variations
  "tech": { en: "Tech", ar: "تقنية" },
  "crypto": { en: "Crypto", ar: "كريبتو" },
  "marketing": { en: "Marketing", ar: "تسويق" },
  "business": { en: "Business", ar: "أعمال" },
  "news": { en: "News", ar: "أخبار" },
  "entertainment": { en: "Entertainment", ar: "ترفيه" },
  "technology": { en: "Technology", ar: "تقنية" },
  "finance": { en: "Finance", ar: "مالية" },
  "trading": { en: "Trading", ar: "تجارة" }
};

// Mini Interests Chart Component
const InterestsChart = ({ interests, isRTL }) => {
  if (!interests || interests.length === 0) return null;
  
  const maxCount = Math.max(...interests.map(i => i.count));
  
  return (
    <div className="mt-3 p-2 bg-black/20 rounded-lg">
      <div className="flex items-center gap-1 mb-2">
        <TrendingUp size={12} style={{ color: '#00F0FF' }} />
        <span style={{ color: '#00F0FF', fontSize: '0.65rem', fontWeight: '600' }}>
          {isRTL ? 'أبرز الاهتمامات' : 'Top Interests'}
        </span>
      </div>
      <div className="space-y-1.5">
        {interests.map((interest, idx) => {
          const percentage = (interest.count / maxCount) * 100;
          const name = categoryNames[interest.id];
          const displayName = name ? (isRTL ? name.ar : name.en) : interest.id;
          
          return (
            <div key={idx} className="flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: interest.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span 
                    className="truncate"
                    style={{ color: '#FFFFFF', fontSize: '0.6rem', maxWidth: '60%' }}
                  >
                    {displayName}
                  </span>
                  <span 
                    className="font-mono"
                    style={{ color: interest.color, fontSize: '0.6rem', fontWeight: '600' }}
                  >
                    {interest.count.toLocaleString()}
                  </span>
                </div>
                <div 
                  className="h-1 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${percentage}%`, 
                      backgroundColor: interest.color,
                      boxShadow: `0 0 6px ${interest.color}50`
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getPlatformsData = (t, isRTL) => [
  {
    id: 'telegram',
    name: 'Telegram',
    nameLocal: isRTL ? 'تلجرام' : 'Telegram',
    icon: TelegramIcon,
    color: '#00F0FF',
    bgColor: 'rgba(0, 240, 255, 0.1)',
    description: t('platforms.monitorGroups'),
    features: [t('platforms.groupTracking'), t('platforms.memberTracking'), t('platforms.messageAnalysis'), t('platforms.smartClassification')],
    status: 'ready',
    requiresKeys: ['API_ID', 'API_HASH', 'PHONE'],
    docsUrl: 'https://my.telegram.org'
  },
  {
    id: 'twitter',
    name: 'X (Twitter)',
    nameLocal: isRTL ? 'إكس (تويتر)' : 'X (Twitter)',
    icon: XTwitterIcon,
    color: '#1DA1F2',
    bgColor: 'rgba(29, 161, 242, 0.1)',
    description: t('platforms.analyzeTweets'),
    features: [t('platforms.trackHashtags'), t('platforms.followerAnalysis'), t('platforms.accountMonitoring'), t('platforms.sentimentAnalysis')],
    status: 'coming_soon',
    requiresKeys: ['API_KEY', 'API_SECRET', 'ACCESS_TOKEN', 'ACCESS_SECRET'],
    docsUrl: 'https://developer.twitter.com',
    cost: isRTL ? '$100-5000/شهر' : '$100-5000/month'
  },
  {
    id: 'google_maps',
    name: 'Google Maps',
    nameLocal: isRTL ? 'خرائط جوجل' : 'Google Maps',
    icon: GoogleMapsIcon,
    color: '#34A853',
    bgColor: 'rgba(52, 168, 83, 0.1)',
    description: t('platforms.collectBusinessData'),
    features: [t('platforms.businessSearch'), t('platforms.collectReviews'), t('platforms.contactInfo'), t('platforms.competitorAnalysis')],
    status: 'coming_soon',
    requiresKeys: ['GOOGLE_API_KEY'],
    docsUrl: 'https://console.cloud.google.com',
    cost: isRTL ? 'مجاني حتى $200/شهر' : 'Free up to $200/month'
  },
  {
    id: 'facebook',
    name: 'Facebook Pages',
    nameLocal: isRTL ? 'صفحات فيسبوك' : 'Facebook Pages',
    icon: FacebookIcon,
    color: '#1877F2',
    bgColor: 'rgba(24, 119, 242, 0.1)',
    description: t('platforms.monitorPages'),
    features: [t('platforms.pageInsights'), t('platforms.postAnalysis'), t('platforms.commentMonitoring'), t('platforms.competitorAnalysis')],
    status: 'coming_soon',
    requiresKeys: ['APP_ID', 'APP_SECRET', 'ACCESS_TOKEN'],
    docsUrl: 'https://developers.facebook.com',
    cost: isRTL ? 'مجاني (يتطلب موافقة)' : 'Free (requires approval)'
  }
];

const PlatformCard = ({ platform, config, onConfigure, onToggle, t, isRTL, isLight }) => {
  const Icon = platform.icon;
  const isConfigured = config?.is_configured;
  const isActive = config?.is_active;
  const isReady = platform.status === 'ready';
  const isComingSoon = platform.status === 'coming_soon';
  const topInterests = config?.stats?.top_interests || [];
  
  // Check if platform has real data (members > 0) which indicates it's connected
  const hasMembers = config?.stats?.members > 0;
  // Consider connected if: is_active OR is_configured OR has members
  const isConnected = isActive || isConfigured || hasMembers;

  return (
    <div 
      data-testid={`platform-card-${platform.id}`}
      className={`stat-card card-hover relative overflow-hidden ${
        isComingSoon ? 'opacity-75' : ''
      }`}
      style={{ borderColor: isActive ? `${platform.color}50` : 'transparent' }}
    >
      {/* Status Badge */}
      <div className="absolute top-4 left-4">
        {isComingSoon ? (
          <span className="badge bg-yellow-500/20 text-yellow-400">{t('platforms.comingSoon')}</span>
        ) : isConnected ? (
          <span className="badge badge-success">{t('common.active')}</span>
        ) : (
          <span className="badge bg-neutral-700 text-neutral-400">{t('platforms.notConnected')}</span>
        )}
      </div>

      {/* Icon & Name */}
      <div className="flex items-center gap-4 mb-4">
        <div 
          className={`w-14 h-14 rounded-xl flex items-center justify-center platform-icon-${platform.id}`}
          style={{ background: platform.bgColor }}
        >
          <Icon size={28} style={{ color: platform.color }} />
        </div>
        <div>
          <h3 style={{ color: '#FFFFFF', fontSize: '1.125rem', fontWeight: '700' }}>{platform.nameLocal}</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>{platform.name}</p>
        </div>
      </div>

      {/* Description */}
      <p style={{ color: '#FFFFFF', fontSize: '0.875rem', marginBottom: '1rem' }}>{platform.description}</p>

      {/* Features */}
      <div className="flex flex-wrap gap-2 mb-4">
        {platform.features.slice(0, 3).map((feature, idx) => (
          <span 
            key={idx}
            className="platform-feature-tag"
            style={{ 
              color: '#FFFFFF', 
              fontSize: '0.75rem',
              padding: '0.25rem 0.5rem',
              borderRadius: '9999px',
              background: 'rgba(255,255,255,0.1)'
            }}
          >
            {feature}
          </span>
        ))}
      </div>

      {/* Stats (if has members or configured) */}
      {(isConfigured || (config?.stats?.members > 0)) && (
        <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-black/30 rounded-lg">
          <div className="text-center">
            <Users size={16} className="mx-auto mb-1" style={{ color: '#FFFFFF' }} />
            <p className="font-mono text-lg" style={{ color: '#FFFFFF', fontWeight: '700' }}>
              {(config?.stats?.members || 0).toLocaleString()}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>{t('members.title')}</p>
          </div>
          <div className="text-center">
            <Activity size={16} className="mx-auto mb-1" style={{ color: '#FFFFFF' }} />
            <p className="font-mono text-lg" style={{ color: '#FFFFFF', fontWeight: '700' }}>
              {(config?.stats?.activities || 0).toLocaleString()}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>{t('members.activities')}</p>
          </div>
          <div className="text-center">
            <MessageCircle size={16} className="mx-auto mb-1" style={{ color: '#FFFFFF' }} />
            <p className="font-mono text-lg" style={{ color: '#FFFFFF', fontWeight: '700' }}>
              {(config?.stats?.interests || 0).toLocaleString()}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem', lineHeight: '1.2' }}>
              {t('dashboard.totalCategories')}
            </p>
          </div>
        </div>
      )}

      {/* Mini Interests Chart */}
      {topInterests.length > 0 && (
        <InterestsChart interests={topInterests} isRTL={isRTL} />
      )}

      {/* Cost (if any) */}
      {platform.cost && (
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginBottom: '1rem' }}>
          💰 {t('platforms.cost')}: {platform.cost}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        {isComingSoon ? (
          <Button 
            variant="secondary" 
            className="flex-1 platform-btn-hover coming-soon-btn" 
            disabled
          >
            <Lock size={16} className="ml-2" />
            {t('platforms.comingSoon')}
          </Button>
        ) : (
          <>
            <Button 
              data-testid={`configure-${platform.id}`}
              variant={isConfigured ? "ghost" : "default"}
              className={`flex-1 ${isConfigured ? 'platform-btn-hover' : ''}`}
              onClick={() => onConfigure(platform)}
              style={isConfigured ? { 
                border: '2px solid #FFFFFF',
                background: 'transparent',
                color: '#FFFFFF',
                transition: 'all 0.3s ease'
              } : { background: platform.color, color: '#000' }}
            >
              <Settings size={16} className="ml-2" />
              {isConfigured ? t('settings.title') : t('platforms.setup')}
            </Button>
            {isConfigured && (
              <Button
                data-testid={`toggle-${platform.id}`}
                variant={isActive ? "destructive" : "default"}
                size="icon"
                onClick={() => onToggle(platform.id, !isActive)}
              >
                {isActive ? <Pause size={16} /> : <Play size={16} />}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Docs Link */}
      <a 
        href={platform.docsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-300 mt-4 transition-colors"
      >
        <ExternalLink size={12} />
        {t('platforms.requiresApiKeys')}
      </a>
    </div>
  );
};

const ConfigureModal = ({ platform, config, onSave, onClose, t }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config?.credentials) {
      setForm(config.credentials);
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(platform.id, form);
    setSaving(false);
  };

  if (!platform) return null;

  const Icon = platform.icon;

  return (
    <Dialog open={!!platform} onOpenChange={onClose}>
      <DialogContent className="glass border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: platform.bgColor }}
            >
              <Icon size={20} style={{ color: platform.color }} />
            </div>
            {t('platforms.setup')} {platform.nameLocal}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Instructions */}
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
            <p className="text-sm text-cyan-300">
              {t('platforms.viewDocs')}:
            </p>
            <a 
              href={platform.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-cyan-400 hover:underline flex items-center gap-1 mt-1"
            >
              <ExternalLink size={14} />
              {platform.docsUrl}
            </a>
          </div>

          {/* API Keys Fields */}
          {platform.requiresKeys.map(key => (
            <div key={key}>
              <label className="text-sm text-neutral-400 block mb-2">{key}</label>
              <Input
                data-testid={`input-${key.toLowerCase()}`}
                type={key.includes('SECRET') || key.includes('HASH') ? 'password' : 'text'}
                placeholder={`${t('common.add')} ${key}`}
                value={form[key] || ''}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                dir="ltr"
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            data-testid="save-platform-config"
            onClick={handleSave}
            disabled={saving}
            style={{ background: platform.color, color: '#000' }}
          >
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Platforms() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [configuringPlatform, setConfiguringPlatform] = useState(null);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { isLight } = useTheme();
  
  const platformsData = getPlatformsData(t, isRTL);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      const configsMap = {};
      response.data.forEach(c => {
        configsMap[c.platform_id] = c;
      });
      setConfigs(configsMap);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleConfigure = (platform) => {
    setConfiguringPlatform(platform);
  };

  const handleSaveConfig = async (platformId, credentials) => {
    try {
      await axios.post(`${API}/platforms/${platformId}/configure`, { credentials });
      toast.success(t('platforms.platformConnected'));
      setConfiguringPlatform(null);
      fetchConfigs();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleToggle = async (platformId, activate) => {
    try {
      await axios.post(`${API}/platforms/${platformId}/${activate ? 'activate' : 'deactivate'}`);
      toast.success(activate ? t('platforms.platformConnected') : t('platforms.platformDisconnected'));
      fetchConfigs();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  // Count stats
  const configuredCount = Object.values(configs).filter(c => c.is_configured).length;
  const activeCount = Object.values(configs).filter(c => c.is_active).length;

  return (
    <div data-testid="platforms-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-wide">{t('platforms.title')}</h1>
        <p className="text-neutral-500 mt-1">{t('platforms.subtitle')}</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <Settings size={24} className="text-cyan-400" />
          </div>
          <div>
            <p style={{ color: '#FFFFFF', fontSize: '0.875rem' }}>{t('platforms.title')}</p>
            <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontFamily: "'JetBrains Mono', monospace", fontWeight: '700' }}>{platformsData.length}</p>
          </div>
        </div>
        <div className="stat-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Unlock size={24} className="text-green-400" />
          </div>
          <div>
            <p style={{ color: '#FFFFFF', fontSize: '0.875rem' }}>{t('platforms.configure')}</p>
            <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontFamily: "'JetBrains Mono', monospace", fontWeight: '700' }}>{configuredCount}</p>
          </div>
        </div>
        <div className="stat-card flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Activity size={24} className="text-violet-400" />
          </div>
          <div>
            <p style={{ color: '#FFFFFF', fontSize: '0.875rem' }}>{t('common.active')}</p>
            <p style={{ color: '#FFFFFF', fontSize: '1.5rem', fontFamily: "'JetBrains Mono', monospace", fontWeight: '700' }}>{activeCount}</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="stat-card border-cyan-500/30">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={20} className="text-cyan-400" />
          </div>
          <div>
            <h4 style={{ color: '#FFFFFF', fontWeight: '600' }}>{t('app.name')}</h4>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {t('platforms.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Platforms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platformsData.map(platform => (
          <PlatformCard
            key={platform.id}
            platform={platform}
            config={configs[platform.id]}
            onConfigure={handleConfigure}
            onToggle={handleToggle}
            t={t}
            isRTL={isRTL}
            isLight={isLight}
          />
        ))}
      </div>

      {/* Configure Modal */}
      <ConfigureModal
        platform={configuringPlatform}
        config={configs[configuringPlatform?.id]}
        onSave={handleSaveConfig}
        onClose={() => setConfiguringPlatform(null)}
        t={t}
      />
    </div>
  );
}
